import {File, RestoreTier} from './types-repository';

/*
 * How long a restore will still take, in words (VTK-3472). S3 gives no per-object ETA -- only the tier,
 * the object's size and how long the restore has been running -- so this never names a clock time.
 * The same table is in content-repository-cdk/content_repository_api_restore_lambdas/restore_utils_layer/
 * restore_utils.py (RESTORE_WORDING_BANDS); keep the two in step.
 *
 * Bands per row of the table: [running for less than N minutes, text]; the last band includes its N,
 * past it the restore is late.
 */
export const RESTORE_WORDING_UNKNOWN = 'in a few hours';
export const RESTORE_WORDING_LATE = 'taking longer than usual';
const EXPEDITED_SMALL_OBJECT_BYTES = 250 * 1024 * 1024;
const RESTORE_WORDING_BANDS: Record<string, [number, string][]> = {
  'Expedited': [[5, 'in a few minutes']],  // objects up to EXPEDITED_SMALL_OBJECT_BYTES
  'Expedited, large': [[60, 'within the hour']],
  'Standard': [[60, 'in a few hours'], [3 * 60, 'in an hour or two'], [5 * 60, 'soon']],
  'Bulk': [[4 * 60, 'within half a day'], [10 * 60, 'in a few hours'], [12 * 60, 'soon']],
  'Deep Archive Standard': [[12 * 60, 'within a day']],
  'Deep Archive Bulk': [[48 * 60, 'within two days']],
};

// The API's tier -> the AWS tier name the table is keyed by.
const AWS_TIER: Record<RestoreTier, string> = {
  [RestoreTier.RestoreTier_1]: 'Expedited',
  [RestoreTier.RestoreTier_2]: 'Standard',
  [RestoreTier.RestoreTier_3]: 'Bulk',
};

export interface RestoreProgress {
  tier?: RestoreTier | null;
  runningMinutes?: number | null;  // since the restore was requested
  size?: number | null;  // bytes
  deepArchive?: boolean | null;
}

/** [text, minutes left until the row's usual maximum]; minutes is null when nothing is known. */
function estimate(restore: RestoreProgress): [string, number | null] {
  const tier = restore.tier ? AWS_TIER[restore.tier] : undefined;
  let row = (restore.deepArchive ? 'Deep Archive ' : '') + tier;
  if (row === 'Expedited' && (restore.size ?? 0) > EXPEDITED_SMALL_OBJECT_BYTES) {
    row = 'Expedited, large';
  }
  const bands = RESTORE_WORDING_BANDS[row];
  const minutes = restore.runningMinutes;
  if (!bands || minutes == null) {
    return [RESTORE_WORDING_UNKNOWN, null];
  }
  const [maximum, lastText] = bands[bands.length - 1];
  for (const [limit, text] of bands.slice(0, -1)) {
    if (minutes < limit) {
      return [text, maximum - minutes];
    }
  }
  return minutes <= maximum ? [lastText, maximum - minutes] : [RESTORE_WORDING_LATE, Infinity];
}

/**
 * The wording for one or several restores; the slowest one decides. A late restore is the slowest; one
 * about which nothing is known only decides when nothing is known about any.
 */
export function restoreWording(restores: RestoreProgress[]): string {
  const known = restores.map(estimate).filter(([, left]) => left !== null) as [string, number][];
  if (!known.length) {
    return RESTORE_WORDING_UNKNOWN;
  }
  return known.reduce((slowest, e) => e[1] > slowest[1] ? e : slowest)[0];
}

/** The RestoreProgress of a file that is being restored, from its archive state. */
export function restoreProgressOf(file: File, now: Date = new Date()): RestoreProgress {
  const extra = file.archive?.extra;
  const requested = extra?.restore_requested ? new Date(extra.restore_requested).valueOf() : NaN;
  return {
    tier: extra?.restore_tier,
    runningMinutes: isNaN(requested) ? null : (now.valueOf() - requested) / 60_000,
    size: file.size,
    deepArchive: extra?.deep_archive,
  };
}
